import React, { useState } from "react";
import { Box, FormControl, Typography, InputLabel, MenuItem, Select, Tooltip, useTheme, Divider } from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import { useAlert } from "../../../global/AlertContext";
import { useNavigate } from "react-router-dom";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import InfoIcon from "@mui/icons-material/Info";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import SuccessButton from "../../../components/SuccessButton";
import { useGetLotesQuery, useGetLoteByIdQuery } from "../../../services/loteService";
import { useGetTicketsQuery } from "../../../services/ticketService";
import { useCreateApplicationMutation } from "../../../services/applicationService";
import Header from "../../../components/Header";
import LoadingComponent from "../../../components/LoadingComponent";
import ErrorComponent from "../../../components/ErrorComponent";
import SearchSelect from "../../../components/SearchSelect"; // Import the SearchSelect component
import CancelButton from "../../../components/CancelButton";
import dayjs from 'dayjs'; // Import dayjs
import { tokens } from "../../../theme";
import DosagesList from "../../../components/DosagesList";

const CreateApplication = () => {
  const showAlert = useAlert();
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [createApplication] = useCreateApplicationMutation();
  const [searchTermLotes, setSearchTermLotes] = useState("");
  const [searchTermTickets, setSearchTermTickets] = useState("");
  const [selectedLoteId, setSelectedLoteId] = useState(null);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  
  const { data: lotes, error: lotesError, isLoading: isLoadingLotes } = useGetLotesQuery({
    name: searchTermLotes, // Include search term for lotes
  });

  const { data: tickets, error: ticketsError, isLoading: isLoadingTickets } = useGetTicketsQuery({
    name: searchTermTickets,
  });

  const { data: loteDetails } = useGetLoteByIdQuery(selectedLoteId, {
    skip: !selectedLoteId, // Skip the query if no lote is selected
  });

  const handleFormSubmit = async (application, { resetForm }) => {
    const toCreateApplication = {
      ...application,
      programmed_date: application.programmed_date ? dayjs(application.programmed_date).format('YYYY-MM-DD') : null, // Format as 'YYYY-MM-DD'
    };
    try {
      await createApplication(toCreateApplication).unwrap();
      showAlert("Aplicación creada!", "success");
      resetForm();
      navigate("/applications");
    } catch (error) {
      showAlert(error.message, "error");
    }
  };

  const initialValues = {
    lote_id: "",
    programmed_date: null,
    ticket_id: "",
    blocks: []
  };

  const applicationSchema = yup.object().shape({
    lote_id: yup.string().required("Requerido"),
    programmed_date: yup
      .date()
      .nullable()
      .typeError("Ingrese una fecha válida")
      .required("Requerido"),
    ticket_id: yup.string().required("Requerido"),
    blocks: yup
      .array()
      .min(1, "Seleccione al menos un bloque")
      .required("Requerido")
  });

  if (isLoadingLotes) {
    return <LoadingComponent />;
  }

  if (lotesError) {
    return <ErrorComponent />;
  }

  return (
    <Box m="20px">
      <Header title="Aplicaciones" subtitle="Crear Aplicación Extraordinaria" />
      <Formik
        onSubmit={(values, { resetForm }) => handleFormSubmit(values, { resetForm })}
        initialValues={initialValues}
        validationSchema={applicationSchema}
      >
        {({ values, errors, touched, handleBlur, handleChange, handleSubmit, setFieldValue }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="30px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
            >

              {/* Ticket SearchSelect */}
              <Box sx={{ gridColumn: "span 2" }}>
                
                <Box display="flex" alignItems="center" gap={1}>
                  <Tooltip 
                    title="El día del ticket no es tomado en cuenta al crear aplicación extraordinaria" 
                    arrow
                    slotProps={{
                      tooltip: {
                        sx: {
                          fontSize: '0.9rem',
                          textAlign: 'center'
                        },
                      },
                    }}
                  >
                    <InfoIcon sx={{ color: colors.greenAccent[500], cursor: "pointer"}} />
                  </Tooltip>
                  <SearchSelect
                    label="Ticket"
                    options={tickets?.records || []}
                    value={tickets?.records.find((ticket) => ticket.id === values.ticket_id) || null}
                    onChange={(newValue) => {
                      setFieldValue("ticket_id", newValue ? newValue.id : "");
                      setSelectedTicketId(newValue ? newValue.id : null); // Update selected lote id
                    }}
                    onInputChange={(newInputValue) => {
                      setSearchTermTickets(newInputValue); // Update search term for tickets
                    }}
                    getOptionLabel={(option) => option.name}
                    error={!!touched.ticket_id && !!errors.ticket_id}
                    helperText={touched.ticket_id && errors.ticket_id}
                    isLoading={isLoadingTickets}
                    noOptionsText={ticketsError ? "Error al cargar tickets" : "No se encontraron tickets"}
                  />
                </Box>
              </Box>

              {/* Date Select */}
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Fecha Programada"
                  value={values.programmed_date ? dayjs(values.programmed_date) : null} // Convert to Day.js object
                  onChange={(newValue) => setFieldValue("programmed_date", newValue)} // Set as Day.js object
                  format="DD/MM/YYYY" // Set user-friendly format
                  slotProps={{
                    textField: {
                      variant: "filled",
                      fullWidth: true,
                      error: !!touched.programmed_date && !!errors.programmed_date,
                      helperText: touched.programmed_date && errors.programmed_date,
                      sx: { gridColumn: "span 2" },
                    },
                  }}
                />
              </LocalizationProvider>

              {/* Lote SearchSelect */}
              <Box sx={{ gridColumn: "span 2" }}>
                <SearchSelect
                  label="Lote"
                  options={lotes?.records || []}
                  value={lotes?.records.find((lote) => lote.id === values.lote_id) || null}
                  onChange={(newValue) => {
                    setFieldValue("lote_id", newValue ? newValue.id : "");
                    setSelectedLoteId(newValue ? newValue.id : null); // Update selected lote id
                  }}
                  onInputChange={(newInputValue) => {
                    setSearchTermLotes(newInputValue); // Update search term for lotes
                  }}
                  getOptionLabel={(option) => option.name}
                  error={!!touched.lote_id && !!errors.lote_id}
                  helperText={touched.lote_id && errors.lote_id}
                  isLoading={isLoadingLotes}
                  noOptionsText={lotesError ? "Error al cargar lotes" : "No se encontraron lotes"}
                />
              </Box>

              {/* Block Selection */}
              <FormControl fullWidth variant="filled" sx={{ gridColumn: "span 2" }}>
                <InputLabel id="blocks-label">Seleccionar Bloques</InputLabel>
                <Select
                  disabled={!selectedLoteId || loteDetails?.blocks.length === 0}
                  labelId="blocks-label"
                  multiple
                  value={values.blocks}
                  onChange={(e) => {
                    handleChange(e); // Formik's handleChange
                    setFieldValue("blocks", e.target.value); // Update the field value
                  }}
                  onBlur={handleBlur} // Formik's handleBlur
                  name="blocks" // Ensure the name matches the field name in Formik
                  renderValue={(selected) =>
                    selected
                      .map((id) => {
                        const block = loteDetails?.blocks?.find((b) => b.id === id);
                        const isBaseBlock = loteDetails?.baseBlocks?.some((b) => b.id === block?.id);
                        return block ? `${block.number}${isBaseBlock ? "" : " (forzado)"}` : "";
                      })
                      .join(", ")
                  }
                  error={!!touched.blocks && !!errors.blocks}
                >
                  {loteDetails?.blocks?.map((block) => {
                    const isBaseBlock = loteDetails?.baseBlocks?.some((b) => b.id === block.id);
                    return (
                      <MenuItem key={block.id} value={block.id}>
                        {block.number} {isBaseBlock ? "" : "(forzado)"}
                      </MenuItem>
                    );
                  })}
                </Select>
                {touched.blocks && errors.blocks && (
                  <Typography variant="caption" color="error">
                    {errors.blocks}
                  </Typography>
                )}
              </FormControl>

            </Box>

            <Divider sx={{ my: "20px", borderColor: colors.grey[700] }} />

            <Typography variant="h5" fontWeight="bold" mb="16px" color={colors.grey[100]}>
              Dosificación
            </Typography>
            <DosagesList ticketId={selectedTicketId} isReadOnly />
            <Box
              display="flex"
              justifyContent="end"
              mt="2vh"
              gap="4px"
            >
              <CancelButton label="Cancelar" onClick={() => navigate('/applications')}/>
              <SuccessButton label="Crear Aplicación" />
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default CreateApplication;